import React, { useState } from 'react';
import dayjs from 'dayjs';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useEnv } from '../contexts/EnvContext';
import { removeVideoRecording, viewVideoRecording } from '../api';

const when = (d) => (d ? dayjs(d).format('MMM D, YYYY h:mm A') : '—');
const length = (ms) => {
  const total = Math.round(Number(ms || 0) / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

/**
 * A call's recordings for staff (admin panel plan RPT-21): one MP4 per
 * stretch the two were together, listed in the order they were made. Bidsy
 * signs each link with its own key and stores the viewing against the staff
 * member; the panel only plays it, with no download control. A call with no
 * report or case asks why before it plays. Remove takes every stretch of the
 * call: there is no remove button in the app, so a person's request by email
 * ends here.
 */
export default function RecordingList({ recordings, call = null, fan = null, creator = null }) {
  const { env } = useEnv();
  const [playing, setPlaying] = useState(null);
  const [error, setError] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  const [askingWhy, setAskingWhy] = useState(null);
  const [watchReason, setWatchReason] = useState('');
  const [removing, setRemoving] = useState(false);
  const [requestedBy, setRequestedBy] = useState('fan');
  const [removeReason, setRemoveReason] = useState('');
  const [removed, setRemoved] = useState(null);
  const playable = [...(recordings || [])].sort((a, b) => a.id - b.id).filter((r) => r.status === 'complete' && !r.deleted_at);

  const watch = async (recording) => {
    setError(null);
    setLoadingId(recording.id);
    try {
      const why = watchReason.trim();
      const answer = await viewVideoRecording(recording.id, ...(why ? [why] : []));
      setAskingWhy(null);
      setPlaying({ id: recording.id, url: answer.data.url });
    } catch (e) {
      setPlaying(null);
      if (e?.response?.data?.code === 'VIDEO_RECORDING_REASON_REQUIRED') {
        setAskingWhy(recording);
        return;
      }
      setError({
        // A recording Bidsy says is not available is an answer; anything else is a fault.
        severity: e?.response?.status === 404 ? 'info' : 'error',
        text: e?.response?.data?.message || e?.message || 'The recording could not be opened.',
      });
    } finally {
      setLoadingId(null);
    }
  };

  const remove = async () => {
    setError(null);
    try {
      const answer = await removeVideoRecording(playable[0].id, { requested_by: requestedBy, reason: removeReason.trim() });
      setRemoved(answer.data);
      setRemoving(false);
    } catch (e) {
      setRemoving(false);
      setError({ severity: 'error', text: e?.response?.data?.message || e?.message || 'The recording could not be removed.' });
    }
  };

  // The stretches play in order: when one ends, the next playable one opens.
  const next = playing ? playable.find((r) => r.id > playing.id) : null;

  // The recording is LiveKit's two-tile grid with the earlier joiner on the left; the call's timeline says who that
  // was when the stretch began. shortcut: a side that drops and rejoins mid-recording moves to the right half, which
  // this cannot see; label per rejoin only if staff report mislabeled halves.
  const sidesOf = (recordingId) => {
    const sides = [{ word: 'Fan', name: fan?.username }, { word: 'Creator', name: creator?.username }];
    const row = playable.find((r) => r.id === recordingId);
    const ms = row?.started_at && call?.starts_at ? new Date(row.started_at) - new Date(call.starts_at) : NaN;
    const joinedAt = (stretches) => (stretches || []).filter((s) => s.start <= ms).pop()?.start;
    const [fanIn, creatorIn] = [joinedAt(call?.timeline?.fan), joinedAt(call?.timeline?.creator)];
    return fanIn != null && creatorIn != null && creatorIn < fanIn ? sides.reverse() : sides;
  };

  if (!recordings?.length) return <Typography variant="body2" color="text.secondary">No recording.</Typography>;
  return (
    <>
      {error ? <Alert severity={error.severity} onClose={() => setError(null)} sx={{ mb: 1 }}>{error.text}</Alert> : null}
      {removed ? (
        <Alert severity="success" sx={{ mb: 1 }}>
          Removed. Viewing ends {when(removed.delete_after)}.{removed.held_by_case ? ' The files stay until the open case is settled.' : ''}
        </Alert>
      ) : null}
      <Table size="small" aria-label="recordings">
        <TableHead>
          <TableRow>
            <TableCell>Recording</TableCell>
            <TableCell>Started</TableCell>
            <TableCell>Length</TableCell>
            <TableCell>State</TableCell>
            <TableCell>Kept until</TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {[...recordings].sort((a, b) => a.id - b.id).map((recording) => (
            <TableRow key={recording.id}>
              <TableCell>#{recording.id}</TableCell>
              <TableCell>{when(recording.started_at)}</TableCell>
              <TableCell>{length(recording.duration_ms)}</TableCell>
              <TableCell>{recording.deleted_at ? 'deleted' : recording.status}</TableCell>
              <TableCell>{removed?.delete_after || recording.delete_after ? when(removed?.delete_after || recording.delete_after) : 'Until removed'}</TableCell>
              <TableCell align="right">
                {recording.status === 'complete' && !recording.deleted_at ? (
                  <Button size="small" variant="outlined" disabled={loadingId === recording.id} onClick={() => watch(recording)}>Watch</Button>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {playable.length && !removed ? (
        <Button size="small" color="error" sx={{ mt: 1 }} onClick={() => setRemoving(true)}>Remove recording…</Button>
      ) : null}
      <Dialog open={Boolean(askingWhy)} onClose={() => setAskingWhy(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Why are you watching this call?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This call has no report or case. Staff watch a recording only for a report, a case or a legal request. Your reason is stored against your name.
          </Typography>
          <TextField fullWidth multiline minRows={2} label="Reason" value={watchReason} onChange={(event) => setWatchReason(event.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAskingWhy(null)}>Cancel</Button>
          <Button variant="contained" disabled={!watchReason.trim()} onClick={() => watch(askingWhy)}>Watch</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={removing} onClose={() => setRemoving(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Remove this call&apos;s recording?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This removes every part of the recording for both people and can&apos;t be undone. Grant a fan&apos;s request once you&apos;ve
            confirmed it came from a contact on the fan&apos;s account. Remove a recording at a creator&apos;s request only if the law
            requires it, the call broke the rules, or someone&apos;s safety is at risk, and then tell the fan. Don&apos;t remove anything
            under a legal hold.
          </Typography>
          <TextField select fullWidth SelectProps={{ native: true }} label="Who asked" value={requestedBy} onChange={(event) => setRequestedBy(event.target.value)} sx={{ mb: 2 }}>
            <option value="fan">The fan</option>
            <option value="creator">The creator</option>
            <option value="bidsy">Bidsy</option>
          </TextField>
          <TextField fullWidth multiline minRows={2} label="Why" value={removeReason} onChange={(event) => setRemoveReason(event.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoving(false)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={!removeReason.trim()} onClick={remove}>
            Remove on {env === 'development' ? 'DEV' : 'PROD'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(playing)} onClose={() => setPlaying(null)} maxWidth="md" fullWidth>
        <DialogTitle>Recording #{playing?.id}</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            This viewing is recorded against your name.{next ? ` Recording #${next.id} plays next.` : ''}
          </Typography>
          {playing && fan && creator ? (
            <Box sx={{ display: 'flex', mb: 0.5 }} data-testid="recording-sides">
              {sidesOf(playing.id).map((side) => (
                <Typography key={side.word} variant="subtitle2" sx={{ flex: 1, textAlign: 'center' }}>
                  {side.name} <Typography component="span" variant="caption" color="text.secondary">({side.word})</Typography>
                </Typography>
              ))}
            </Box>
          ) : null}
          {playing ? (
            <Box
              component="video"
              src={playing.url}
              controls
              autoPlay
              controlsList="nodownload"
              disablePictureInPicture
              onContextMenu={(event) => event.preventDefault()}
              onEnded={() => (next ? watch(next) : null)}
              data-testid="recording-player"
              sx={{ width: '100%', maxHeight: '70vh', borderRadius: 2, bgcolor: '#000' }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
